import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Loader2,
  Receipt,
  Save,
  ShieldCheck,
  StickyNote,
  User,
} from 'lucide-react'
import { usePatients } from '../contexts/PatientsContext'
import { useToast } from '../contexts/ToastContext'
import { ROUTES } from '../routes/routeMap'
import RouteErrorBoundary from '../components/RouteErrorBoundary'
import CollapsibleCard from '../components/patient/CollapsibleCard'
import PatientEligibilityDataModal, {
  EligibilityDataButton,
} from '../components/patient/PatientEligibilityDataModal'
import PatientRulesEnginePanel from '../components/rules-engine/PatientRulesEnginePanel'
import {
  calculateAge,
  formatPatientDate,
  patientAvatarColors,
  patientFullName,
  patientInitials,
  patientMemberIdLabel,
  patientDetailDummyClaims,
  patientDetailDummyEquipment,
  patientDetailDummyPriorAuths,
  PATIENT_COB_OPTIONS,
  PATIENT_GENDER_OPTIONS,
  PATIENT_SUBSCRIBER_RELATIONSHIP_OPTIONS,
  ELIGIBILITY_LABEL,
  type PatientClaimStatus,
  type PatientEquipmentStatus,
  type PatientEquipmentType,
  type PatientPriorAuthStatus,
  type PatientRecord,
  type PatientStatus,
} from '../data/patientData'
import { getEligibilityCheck, type ApiEligibilityCheck } from '../services/eligibilityChecksService'
import { mapEligibilityCheckToPatientRecord } from '../services/mapEligibilityCheckToPatientRecord'
import type { PatientDetailRouteState } from '../types/rulesEngine'
import { patientRecordToBenefitInputs } from '../utils/rulesEngineFields'

const fieldClass =
  'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500'
const labelClass =
  'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5'

const doctorDummyRecord = {
  doctorName: 'Dr. Sandra Lee',
  specialty: 'Pulmonology',
  npi: '1234567890',
  phone: '(210) 555-0133',
  fax: '(210) 555-0177',
}

const patientAttachmentDummyData = [
  {
    id: 'att-001',
    fileName: 'Prescription_Oxygen_2026.pdf',
    category: 'Prescription',
    uploadedOn: '05/24/2026',
    uploadedBy: 'Front Desk',
  },
  {
    id: 'att-002',
    fileName: 'Insurance_Card_Front.jpg',
    category: 'Insurance',
    uploadedOn: '05/22/2026',
    uploadedBy: 'Intake Team',
  },
  {
    id: 'att-003',
    fileName: 'CMN_Form_Signed.pdf',
    category: 'Clinical',
    uploadedOn: '05/18/2026',
    uploadedBy: 'Care Coordinator',
  },
]

function statusDot(status: PatientStatus): string {
  if (status === 'active') return 'bg-green-500'
  if (status === 'on_hold') return 'bg-amber-500'
  if (status === 'pending') return 'bg-blue-500'
  return 'bg-gray-400'
}

function statusLabel(status: PatientStatus): string {
  if (status === 'active') return 'Active'
  if (status === 'on_hold') return 'On hold'
  if (status === 'pending') return 'Pending'
  return 'Inactive'
}

function equipmentTypeBadge(type: PatientEquipmentType): string {
  if (type === 'Rental') return 'bg-amber-100 text-amber-700'
  if (type === 'Supply') return 'bg-blue-100 text-blue-700'
  return 'bg-violet-100 text-violet-700'
}

function equipmentStatusBadge(status: PatientEquipmentStatus): string {
  if (status === 'Active') return 'bg-green-100 text-green-700'
  if (status === 'Returned') return 'bg-gray-100 text-gray-600'
  return 'bg-gray-100 text-gray-600'
}

function claimStatusBadge(status: PatientClaimStatus): string {
  if (status === 'Paid') return 'bg-green-100 text-green-700'
  if (status === 'Pending') return 'bg-amber-100 text-amber-700'
  if (status === 'Denied') return 'bg-rose-100 text-rose-700'
  return 'bg-blue-100 text-blue-700'
}

function priorAuthBadge(status: PatientPriorAuthStatus): string {
  if (status === 'Active') return 'bg-green-100 text-green-700'
  if (status === 'Renew soon') return 'bg-amber-100 text-amber-700'
  if (status === 'Pending') return 'bg-blue-100 text-blue-700'
  return 'bg-rose-100 text-rose-700'
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="text-sm text-gray-800">{label}</span>
      <label className="inline-flex shrink-0 cursor-pointer items-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
          aria-label={label}
        />
        <span
          className={`relative h-6 w-11 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 ${
            checked ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
              checked ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </span>
      </label>
    </div>
  )
}

const PatientDetail = () => {
  const { eligibilityCheckId } = useParams<{ eligibilityCheckId: string }>()
  const location = useLocation()
  const routeState = (location.state as PatientDetailRouteState | null) ?? null
  const { getPatientByEligibilityCheckId, updatePatient } = usePatients()
  const { showToast } = useToast()
  const navigate = useNavigate()

  const [record, setRecord] = useState<PatientRecord | null>(null)
  const [eligibilityCheck, setEligibilityCheck] = useState<ApiEligibilityCheck | null>(null)
  const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(eligibilityCheckId))
  const [loadError, setLoadError] = useState<string | null>(null)
  const [draft, setDraft] = useState<PatientRecord | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!eligibilityCheckId) {
      setRecord(null)
      setDraft(null)
      setEligibilityCheck(null)
      setIsLoading(false)
      return
    }

    let isMounted = true

    async function loadPatientDetail() {
      setIsLoading(true)
      setLoadError(null)

      try {
        const check = await getEligibilityCheck(eligibilityCheckId)
        const mapped = mapEligibilityCheckToPatientRecord(check)
        if (!isMounted) return
        if (!mapped) {
          setLoadError('Unable to map eligibility check data to patient record.')
          setRecord(null)
          setDraft(null)
          return
        }
        setRecord(mapped)
        setDraft(mapped)
        setEligibilityCheck(check)
        setDirty(false)
      } catch (error) {
        if (!isMounted) return
        const message =
          error instanceof Error ? error.message : 'Failed to load patient details from API.'
        setLoadError(message)
        const fallback = getPatientByEligibilityCheckId(eligibilityCheckId)
        if (fallback) {
          setRecord(fallback)
          setDraft(fallback)
          setEligibilityCheck(null)
        } else {
          setRecord(null)
          setDraft(null)
          setEligibilityCheck(null)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    void loadPatientDetail()

    return () => {
      isMounted = false
    }
  }, [eligibilityCheckId, getPatientByEligibilityCheckId])

  const patientContext = useMemo(
    () => (record ? patientRecordToBenefitInputs(record) : {}),
    [record]
  )

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Loader2 size={24} className="mx-auto mb-3 animate-spin text-primary-600" />
          <p className="text-sm text-gray-600">Loading patient details...</p>
        </div>
      </div>
    )
  }

  if (!record) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-600 mb-4">
            We couldn&apos;t find a patient record for eligibility check{' '}
            <span className="font-mono">{eligibilityCheckId}</span>.
          </p>
          {loadError && <p className="text-sm text-rose-600 mb-4">{loadError}</p>}
          <Link
            to={ROUTES.PATIENTS}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft size={14} />
            Back to all patients
          </Link>
        </div>
      </div>
    )
  }

  if (!draft) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Loader2 size={24} className="mx-auto mb-3 animate-spin text-primary-600" />
          <p className="text-sm text-gray-600">Preparing patient form...</p>
        </div>
      </div>
    )
  }

  const colors = patientAvatarColors(record)
  const age = calculateAge(record.dateOfBirth)
  const equipmentRows =
    draft.equipment && draft.equipment.length > 0
      ? draft.equipment
      : patientDetailDummyEquipment
  const claimRows =
    draft.claims && draft.claims.length > 0 ? draft.claims : patientDetailDummyClaims
  const priorAuthRows =
    draft.priorAuths && draft.priorAuths.length > 0
      ? draft.priorAuths
      : patientDetailDummyPriorAuths

  const update = <K extends keyof PatientRecord>(key: K, value: PatientRecord[K]) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev))
    setDirty(true)
  }

  const handleSave = () => {
    if (!draft) return
    updatePatient(draft.id, draft)
    setDirty(false)
    showToast('Patient changes saved.', { type: 'success' })
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {loadError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Could not refresh from API. Showing cached data. {loadError}
        </div>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(ROUTES.PATIENTS)}
            aria-label="Back to patients"
            title="Back to patients"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <ArrowLeft size={16} />
          </button>
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}
            aria-hidden
          >
            {patientInitials(record)}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                {patientFullName(record)}
              </h1>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${statusDot(record.status)}`}
                  aria-hidden
                />
                {statusLabel(record.status)}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              {patientMemberIdLabel(record)
                ? `${patientMemberIdLabel(record)} · `
                : ''}
              DOB {formatPatientDate(record.dateOfBirth)} · {age} yrs
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <EligibilityDataButton
            onClick={() => setEligibilityModalOpen(true)}
            disabled={!eligibilityCheck}
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={14} />
            Save changes
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        <div className="space-y-4 sm:space-y-5">
          <CollapsibleCard
            icon={<User size={16} />}
            iconBgClass="bg-blue-50 text-blue-600"
            title="Demographics"
            subtitle="Personal and contact information"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className={labelClass}>First name *</label>
                <input
                  type="text"
                  value={draft.firstName}
                  onChange={(e) => update('firstName', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Last name *</label>
                <input
                  type="text"
                  value={draft.lastName}
                  onChange={(e) => update('lastName', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Date of birth *</label>
                <input
                  type="date"
                  value={draft.dateOfBirth}
                  onChange={(e) => update('dateOfBirth', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Gender</label>
                <select
                  value={draft.sex ?? ''}
                  onChange={(e) => update('sex', e.target.value)}
                  className={fieldClass}
                >
                  {PATIENT_GENDER_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Address</label>
                <input
                  type="text"
                  value={draft.address ?? ''}
                  onChange={(e) => update('address', e.target.value)}
                  className={fieldClass}
                  placeholder="Street, city, state ZIP"
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  value={draft.phone ?? ''}
                  onChange={(e) => update('phone', e.target.value)}
                  className={fieldClass}
                  placeholder="(000) 000-0000"
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={draft.email ?? ''}
                  onChange={(e) => update('email', e.target.value)}
                  className={fieldClass}
                  placeholder="name@email.com"
                />
              </div>
              <div>
                <label className={labelClass}>Emergency contact</label>
                <input
                  type="text"
                  value={draft.emergencyContact ?? ''}
                  onChange={(e) => update('emergencyContact', e.target.value)}
                  className={fieldClass}
                  placeholder="Name (relationship)"
                />
              </div>
              <div>
                <label className={labelClass}>Emergency phone</label>
                <input
                  type="tel"
                  value={draft.emergencyPhone ?? ''}
                  onChange={(e) => update('emergencyPhone', e.target.value)}
                  className={fieldClass}
                  placeholder="(000) 000-0000"
                />
              </div>
            </div>
          </CollapsibleCard>

          <CollapsibleCard
            icon={<ShieldCheck size={16} />}
            iconBgClass="bg-emerald-50 text-emerald-600"
            title="Insurance & coverage"
            subtitle="Payor, member IDs, and eligibility"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className={labelClass}>Primary payor</label>
                <input
                  type="text"
                  value={draft.primaryPayor}
                  onChange={(e) => update('primaryPayor', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Plan</label>
                <input
                  type="text"
                  value={draft.plan ?? ''}
                  onChange={(e) => update('plan', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Member ID</label>
                <input
                  type="text"
                  value={draft.memberId}
                  onChange={(e) => update('memberId', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Group number</label>
                <input
                  type="text"
                  value={draft.groupNumber ?? ''}
                  onChange={(e) => update('groupNumber', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Coverage effective</label>
                <input
                  type="date"
                  value={draft.coverageEffective ?? ''}
                  onChange={(e) => update('coverageEffective', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Coverage end</label>
                <input
                  type="date"
                  value={draft.coverageEnd ?? ''}
                  onChange={(e) => update('coverageEnd', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>Subscriber relationship</label>
                <select
                  value={draft.subscriberRelationship ?? ''}
                  onChange={(e) => update('subscriberRelationship', e.target.value)}
                  className={fieldClass}
                >
                  {PATIENT_SUBSCRIBER_RELATIONSHIP_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Coordination of benefits</label>
                <select
                  value={draft.coordinationOfBenefits ?? ''}
                  onChange={(e) => update('coordinationOfBenefits', e.target.value)}
                  className={fieldClass}
                >
                  {PATIENT_COB_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 space-y-1">
              <div className="flex items-center justify-between gap-4 py-2.5">
                <span className="text-sm text-gray-800">Eligibility status</span>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    draft.eligibility === 'verified'
                      ? 'bg-green-50 text-green-700'
                      : draft.eligibility === 'expires_soon'
                        ? 'bg-amber-50 text-amber-700'
                        : draft.eligibility === 'lapsed'
                          ? 'bg-rose-50 text-rose-700'
                          : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <CheckCircle2 size={12} />
                  {ELIGIBILITY_LABEL[draft.eligibility]} — {formatPatientDate(draft.lastVerified)}
                </span>
              </div>
              <Toggle
                label="Secondary insurance on file"
                checked={Boolean(draft.secondaryInsurance)}
                onChange={(next) => update('secondaryInsurance', next)}
              />
              <Toggle
                label="Assignment of benefits signed"
                checked={Boolean(draft.aobSigned)}
                onChange={(next) => update('aobSigned', next)}
              />
              <Toggle
                label="HIPAA authorization on file"
                checked={Boolean(draft.hipaaSigned)}
                onChange={(next) => update('hipaaSigned', next)}
              />
            </div>
          </CollapsibleCard>

          <CollapsibleCard
            icon={<User size={16} />}
            iconBgClass="bg-violet-50 text-violet-600"
            title="Doctor information"
            subtitle="Ordering/referring provider details"
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Doctor name
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Specialty
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      NPI
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Phone
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Fax
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-2 py-2.5 text-sm font-medium text-gray-900">
                      {draft.doctorName ?? doctorDummyRecord.doctorName}
                    </td>
                    <td className="px-2 py-2.5 text-sm text-gray-700">
                      {doctorDummyRecord.specialty}
                    </td>
                    <td className="px-2 py-2.5 text-sm text-gray-700 font-mono">
                      {draft.doctorNpi ?? doctorDummyRecord.npi}
                    </td>
                    <td className="px-2 py-2.5 text-sm text-gray-700">
                      {draft.doctorPhone ?? doctorDummyRecord.phone}
                    </td>
                    <td className="px-2 py-2.5 text-sm text-gray-700">
                      {draft.doctorFax ?? doctorDummyRecord.fax}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CollapsibleCard>

          <CollapsibleCard
            icon={<StickyNote size={16} />}
            iconBgClass="bg-amber-50 text-amber-600"
            title="Clinical notes"
          >
            <label className={labelClass}>Notes</label>
            <textarea
              value={draft.clinicalNotes ?? ''}
              onChange={(e) => update('clinicalNotes', e.target.value)}
              rows={5}
              className={`${fieldClass} resize-y leading-relaxed`}
              placeholder="Add clinical notes, physician details, or care coordination context."
            />
          </CollapsibleCard>

        </div>

        <div className="space-y-4 sm:space-y-5">
          <CollapsibleCard
            icon={<Activity size={16} />}
            iconBgClass="bg-blue-50 text-blue-600"
            title="Active equipment"
            subtitle="Rentals and purchases on record"
          >
            <div className="overflow-x-auto">
                <table className="w-full min-w-[520px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        HCPCS
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Item
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Type
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Since
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {equipmentRows.map((eq) => (
                      <tr key={eq.id}>
                        <td className="px-2 py-2.5 text-sm font-medium text-gray-900">
                          {eq.hcpcs}
                        </td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">{eq.item}</td>
                        <td className="px-2 py-2.5">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${equipmentTypeBadge(
                              eq.type
                            )}`}
                          >
                            {eq.type}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">{eq.since}</td>
                        <td className="px-2 py-2.5">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${equipmentStatusBadge(
                              eq.status
                            )}`}
                          >
                            {eq.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </CollapsibleCard>

          <CollapsibleCard
            icon={<Receipt size={16} />}
            iconBgClass="bg-rose-50 text-rose-600"
            title="Recent claims"
            subtitle="Last 6 months of claim activity"
          >
            <div className="overflow-x-auto">
                <table className="w-full min-w-[520px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Claim #
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        DOS
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Billed
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Paid
                      </th>
                      <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {claimRows.map((claim) => (
                      <tr key={claim.id}>
                        <td className="px-2 py-2.5 text-sm font-medium text-gray-900">
                          {claim.claimNumber}
                        </td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">
                          {formatPatientDate(claim.dos)}
                        </td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">
                          {formatCurrency(claim.billed)}
                        </td>
                        <td className="px-2 py-2.5 text-sm text-gray-700">
                          {claim.paid === null ? '—' : formatCurrency(claim.paid)}
                        </td>
                        <td className="px-2 py-2.5">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${claimStatusBadge(
                              claim.status
                            )}`}
                          >
                            {claim.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </CollapsibleCard>

          <CollapsibleCard
            icon={<ClipboardCheck size={16} />}
            iconBgClass="bg-emerald-50 text-emerald-600"
            title="Prior authorizations"
          >
            <ul className="space-y-2.5">
                {priorAuthRows.map((pa) => (
                  <li
                    key={pa.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {pa.description}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        Auth # {pa.authNumber} · Expires {formatPatientDate(pa.expiresOn)}
                      </p>
                    </div>
                    <span
                      className={`inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${priorAuthBadge(
                        pa.status
                      )}`}
                    >
                      {pa.status}
                    </span>
                  </li>
                ))}
              </ul>
          </CollapsibleCard>

          <CollapsibleCard
            icon={<FileText size={16} />}
            iconBgClass="bg-slate-50 text-slate-700"
            title="Documents attachments"
            subtitle="Patient file attachments and supporting documents"
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      File name
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Category
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Uploaded on
                    </th>
                    <th className="px-2 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      Uploaded by
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {patientAttachmentDummyData.map((attachment) => (
                    <tr key={attachment.id}>
                      <td className="px-2 py-2.5 text-sm font-medium text-gray-900">
                        {attachment.fileName}
                      </td>
                      <td className="px-2 py-2.5 text-sm text-gray-700">{attachment.category}</td>
                      <td className="px-2 py-2.5 text-sm text-gray-700">{attachment.uploadedOn}</td>
                      <td className="px-2 py-2.5 text-sm text-gray-700">{attachment.uploadedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleCard>
        </div>
      </div>

      <RouteErrorBoundary title="Rules engine">
        <PatientRulesEnginePanel
          patientId={record.id}
          patientName={patientFullName(record)}
          memberId={record.memberId}
          payor={record.primaryPayor}
          initialTab={routeState?.rulesEngineTab ?? (routeState?.fromEligibility ? 'run' : undefined)}
          initialBenefitInputs={routeState?.benefitInputs}
          initialRuleSetId={routeState?.ruleSetId}
          fromEligibility={routeState?.fromEligibility}
          patientContext={patientContext}
        />
      </RouteErrorBoundary>

      <PatientEligibilityDataModal
        open={eligibilityModalOpen}
        onClose={() => setEligibilityModalOpen(false)}
        eligibilityCheck={eligibilityCheck}
        patientName={patientFullName(record)}
      />
    </div>
  )
}

export default PatientDetail
